import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { CompanyBase, QrScanSource, QrScanType } from 'models';
import { QrScanHelpers } from 'helpers/QrScanHelpers';

import { SaveScanFlow } from 'components/showtime/components/SaveScanFlow';
import { useSavedProfiles } from 'components/showtime/hooks/useSavedProfiles';
import { openScanSheet, scanSheetState } from 'components/showtime/state/scan-sheet';
import { useCompanyProfile } from 'hooks/use-company-profile';
import { useSnapshot } from 'hooks/use-safe-snapshot';

type QrScanGateProps = {
    company: CompanyBase | CompanyBase['id'] | null | undefined;
    type: QrScanType;
    itemId: string;
    title: string;
};

// Rendered on profile, collateral and product pages. It asks for the visitor's email after a printed QR code, and
// opens the notes pill when they come back to something they saved. Next keeps the page mounted when moving to
// another brochure or product, so each item gets a fresh gate.
const QrScanGate = (props: QrScanGateProps) => <ItemQrScanGate key={props.itemId} {...props} />;

const ItemQrScanGate = (props: QrScanGateProps) => {
    const router = useRouter();
    const { company } = useCompanyProfile(props.company);
    const { qrScans } = useSavedProfiles();
    const { open } = useSnapshot(scanSheetState);

    const scanning = Boolean(router.query[QrScanHelpers.gateParam]);
    const test = Boolean(router.query[QrScanHelpers.testParam]);

    // Visitors who come back to something they saved, at any event, can still add or edit their notes on the latest save
    const returningScan = QrScanHelpers.findLatestScan(qrScans, props.type, props.itemId);

    useEffect(() => {
        if (company && returningScan && !open && !scanning) {
            openScanSheet({ company, itemId: props.itemId, scan: returningScan, startCollapsed: true });
        }
    }, [company, returningScan?.id, open, scanning]);

    // ScanNotesSheetHost shows the sheet, so the gate only renders the scan flow
    if (!company || open || !scanning) {
        return null;
    }

    return (
        <SaveScanFlow
            {...props}
            company={company}
            source={QrScanSource.QR_CODE}
            test={test}
            onScanSaving={(saving) => openScanSheet({ company, itemId: props.itemId, test, ...saving })}
        />
    );
};

export { QrScanGate };
